/**
 * Platform checkout quote — server-validated breakdown before payment.
 */
import { dataProvider as supabase } from "@/lib/providers/data-provider";

async function resolveTenantId(): Promise<string | undefined> {
  try {
    const { useTenantContext } = await import("@/lib/identity/tenant-context-store");
    const orgId = useTenantContext.getState().activeOrganizationId;
    if (orgId) return orgId;
  } catch {
    // tenant context not ready
  }
  const { data: sessionData } = await supabase.auth.getSession();
  const authId = sessionData.session?.user?.id;
  if (!authId) return undefined;
  const { data } = await supabase
    .from("user_profiles" as never)
    .select("firm_id")
    .eq("auth_id", authId)
    .maybeSingle();
  return (data as { firm_id?: string } | null)?.firm_id;
}

export type CheckoutLineItem = {
  label: string;
  amount_paise: number;
};

export type CheckoutTaxBreakdown = {
  gst_enabled: boolean;
  taxable_paise: number;
  cgst_paise: number;
  sgst_paise: number;
  igst_paise: number;
  total_tax_paise: number;
  gst_rate_pct: number;
  is_inter_state: boolean;
};

export type PlatformCheckoutQuote = {
  quote_id: string;
  item_type: string;
  plan_code: string;
  billing_period: string;
  line_items: CheckoutLineItem[];
  subtotal_paise: number;
  annual_discount_paise: number;
  discount_code: string | null;
  discount_label: string | null;
  discount_id: string | null;
  discount_paise: number;
  credit_adjustment_paise: number;
  pre_tax_paise: number;
  fees_paise: number;
  gst_enabled: boolean;
  tax: CheckoutTaxBreakdown;
  payable_paise: number;
  currency: string;
  metadata?: Record<string, unknown>;
};

export type CheckoutQuoteRequest = {
  type?: string;
  plan_code?: string;
  planId?: string;
  credits?: number;
  billing_period?: "monthly" | "annual";
  platform_invoice_id?: string;
  amount_paise?: number;
  discount_code?: string;
  credit_adjustment_paise?: number;
  tenant_id?: string;
  wallet_type?: string;
  return_url?: string;
  customer_email?: string;
};

export function formatCheckoutInr(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

export async function fetchCheckoutQuote(
  payload: CheckoutQuoteRequest,
): Promise<{ ok: true; quote: PlatformCheckoutQuote } | { ok: false; error: string }> {
  try {
    const res = await fetch("/api/payments/quote-checkout.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.ok) {
      return { ok: false, error: body.error ?? "Could not load checkout quote" };
    }
    return { ok: true, quote: body.quote as PlatformCheckoutQuote };
  } catch {
    return { ok: false, error: "Checkout service unavailable" };
  }
}

const SUBSCRIPTION_PLAN_CODES = new Set([
  "avs_10k",
  "avs_30k",
  "avs_50k",
  "enterprise_custom",
  "avs_manufacturing_10k",
  "avs_manufacturing_30k",
  "avs_manufacturing_50k",
]);

function shouldUseSubscriptionCheckout(payload: CheckoutQuoteRequest): boolean {
  const planCode = (payload.plan_code ?? payload.planId ?? "").toLowerCase();
  if (!planCode || planCode === "free_trial" || planCode.startsWith("credits_")) return false;
  if (payload.type === "credits" || payload.type === "invoice") return false;
  if (payload.platform_invoice_id) return false;
  if ((payload.discount_code ?? "").trim() !== "") return false;
  return SUBSCRIPTION_PLAN_CODES.has(planCode) || payload.type === "plan";
}

export async function createCheckoutOrder(
  payload: CheckoutQuoteRequest,
  opts?: { tenantId?: string; customerEmail?: string; customerName?: string; customerPhone?: string },
): Promise<{
  ok: boolean;
  orderId?: string;
  subscriptionId?: string;
  checkoutMode?: "order" | "subscription";
  amountPaise?: number;
  keyId?: string;
  internalPaymentId?: string;
  returnUrl?: string;
  planCode?: string;
  billingPeriod?: string;
  quote?: PlatformCheckoutQuote;
  error?: string;
}> {
  try {
    const tenantId = payload.tenant_id ?? opts?.tenantId ?? (await resolveTenantId());
    const { data: sessionData } = await supabase.auth.getSession();
    const customerEmail = opts?.customerEmail ?? sessionData.session?.user?.email;
    const customerName =
      opts?.customerName ??
      (sessionData.session?.user?.user_metadata?.full_name as string | undefined) ??
      (sessionData.session?.user?.user_metadata?.name as string | undefined);
    const useSubscription = shouldUseSubscriptionCheckout(payload);
    const endpoint = useSubscription
      ? "/api/payments/create-subscription-setup.php"
      : "/api/payments/create-order.php";

    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        tenant_id: tenantId,
        organization_id: tenantId,
        customer_email: customerEmail,
        email: customerEmail,
        name: customerName,
        phone: opts?.customerPhone,
        return_url:
          payload.return_url ??
          (typeof window !== "undefined"
            ? `${window.location.origin}/settings/license?payment=callback`
            : undefined),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      return { ok: false, error: data.error ?? "Could not create payment session" };
    }

    if (useSubscription && data.subscription_id) {
      return {
        ok: true,
        checkoutMode: "subscription",
        subscriptionId: data.subscription_id,
        amountPaise: data.amount_paise,
        keyId: data.key_id,
        internalPaymentId: data.internal_payment_id,
        planCode: data.plan_code,
        billingPeriod: data.billing_period,
      };
    }

    return {
      ok: true,
      checkoutMode: "order",
      orderId: data.razorpay_order_id,
      amountPaise: data.amount_paise,
      keyId: data.key_id,
      internalPaymentId: data.internal_payment_id,
      returnUrl: data.return_url,
      quote: data.quote as PlatformCheckoutQuote | undefined,
    };
  } catch {
    return { ok: false, error: "Payment service unavailable" };
  }
}
